import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface Sube {
  id: string;
  ad: string;
  adres?: string;
  tel?: string;
  email?: string;
  aktif: boolean;
  mobilde_goster: boolean;
  olusturuldu: string;
  yonetici_sayisi: number;
  ogrenci_sayisi: number;
  egitmen_sayisi: number;
  ders_sayisi: number;
}

interface Yonetici {
  id: string;
  sube_id: string;
  email: string;
  izinler?: Record<string, boolean>;
}

const IZIN_ETIKETLER: { key: string; label: string }[] = [
  { key: 'egitmen_yonetim',  label: 'Eğitmen Ekle/Sil' },
  { key: 'hakedis_orani',    label: 'Hakediş Oranı' },
  { key: 'egitmen_atama',    label: 'Eğitmen Ata' },
  { key: 'muhasebe',         label: 'Muhasebe/Kasa' },
  { key: 'kredi_paketleri',  label: 'Kredi Paketleri' },
];

const BOSH_FORM = () => ({ ad: '', adres: '', tel: '', email: '' });

const Subeler: React.FC = () => {
  usePageTitle('Şubeler');
  const { subeGir } = useAuth();
  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';
  const [subeler, setSubeler] = useState<Sube[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [secili, setSecili] = useState<Sube | null>(null);
  const [yoneticiler, setYoneticiler] = useState<Yonetici[]>([]);
  const [modal, setModal] = useState<'yeni' | 'duzenle' | null>(null);
  const [form, setForm] = useState(BOSH_FORM());
  const [yeniYoneticiEmail, setYeniYoneticiEmail] = useState('');
  const [kaydediyor, setKaydediyor] = useState(false);
  const [hata, setHata] = useState('');

  const fetchSubeler = () => {
    api.get('/api/subeler')
      .then(r => setSubeler(r.data))
      .finally(() => setYukleniyor(false));
  };

  useEffect(() => { fetchSubeler(); }, []);

  const secSube = (sube: Sube) => {
    setSecili(sube);
    setYoneticiler([]);
    api.get(`/api/subeler/${sube.id}`).then(r => setYoneticiler(r.data.yoneticiler || []));
  };

  const acModal = (tip: 'yeni' | 'duzenle') => {
    setHata('');
    if (tip === 'duzenle' && secili) {
      setForm({ ad: secili.ad, adres: secili.adres || '', tel: secili.tel || '', email: secili.email || '' });
    } else {
      setForm(BOSH_FORM());
    }
    setModal(tip);
  };

  const kaydet = async () => {
    if (!form.ad.trim()) { setHata('Şube adı zorunlu'); return; }
    setKaydediyor(true);
    setHata('');
    try {
      if (modal === 'yeni') {
        await api.post('/api/subeler', form);
      } else if (modal === 'duzenle' && secili) {
        await api.put(`/api/subeler/${secili.id}`, form);
      }
      setModal(null);
      fetchSubeler();
      if (modal === 'duzenle' && secili) {
        setSecili(prev => prev ? { ...prev, ...form } : null);
      }
    } catch (err: any) {
      setHata(err.response?.data?.hata || 'Bir hata oluştu');
    } finally {
      setKaydediyor(false);
    }
  };

  const silSube = async (id: string) => {
    if (!window.confirm('Bu şubeyi silmek istediğinize emin misiniz?')) return;
    await api.delete(`/api/subeler/${id}`);
    setSecili(null);
    fetchSubeler();
  };

  const yoneticiEkle = async () => {
    if (!yeniYoneticiEmail.trim() || !secili) return;
    try {
      await api.post(`/api/subeler/${secili.id}/yoneticiler`, { email: yeniYoneticiEmail.trim() });
      setYeniYoneticiEmail('');
      const r = await api.get(`/api/subeler/${secili.id}`);
      setYoneticiler(r.data.yoneticiler || []);
      fetchSubeler();
    } catch (err: any) {
      alert(err.response?.data?.hata || 'Eklenemedi');
    }
  };

  const yoneticiSil = async (yoneticiId: string) => {
    if (!secili) return;
    await api.delete(`/api/subeler/${secili.id}/yoneticiler/${yoneticiId}`);
    setYoneticiler(prev => prev.filter(y => y.id !== yoneticiId));
    fetchSubeler();
  };

  const izinToggle = async (yoneticiId: string, izinKey: string, mevcutIzinler: Record<string, boolean>) => {
    if (!secili) return;
    const yeniIzinler = { ...mevcutIzinler, [izinKey]: !mevcutIzinler[izinKey] };
    try {
      await api.patch(`/api/subeler/${secili.id}/yoneticiler/${yoneticiId}/izinler`, { izinler: yeniIzinler });
      setYoneticiler(prev => prev.map(y => y.id === yoneticiId ? { ...y, izinler: yeniIzinler } : y));
    } catch (err: any) {
      alert(err.response?.data?.hata || 'İzin güncellenemedi');
    }
  };

  const giriYap = async (sube: Sube) => {
    try {
      await subeGir(sube.id, sube.ad);
    } catch (err: any) {
      alert(err.response?.data?.hata || 'Giriş başarısız');
    }
  };

  if (yukleniyor) return <div style={{ padding: 40, color: textMuted }}>Yükleniyor...</div>;

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Sol: şube listesi */}
      <div style={{ width: 340, borderRight: `1px solid ${border}`, background: card, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 20px 12px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: textPrimary }}>Şubeler</div>
            <div style={{ fontSize: 12, color: textMuted }}>{subeler.length} şube</div>
          </div>
          <button onClick={() => acModal('yeni')} style={{
            background: '#534AB7', color: 'white', border: 'none', borderRadius: 8,
            padding: '7px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 500,
          }}>
            + Yeni Şube
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {subeler.map(s => (
            <div
              key={s.id}
              onClick={() => secSube(s)}
              style={{
                padding: '12px 14px', borderRadius: 8, marginBottom: 4, cursor: 'pointer',
                background: secili?.id === s.id ? (isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE') : 'transparent',
                border: secili?.id === s.id ? '1px solid #c5c1f5' : '1px solid transparent',
              }}
            >
              <div style={{ fontWeight: 500, fontSize: 14, color: isDark ? '#e0e0e0' : '#333' }}>{s.ad}</div>
              <div style={{ fontSize: 11, color: textSec, marginTop: 4, display: 'flex', gap: 12 }}>
                <span>👤 {s.ogrenci_sayisi} öğrenci</span>
                <span>◐ {s.egitmen_sayisi} eğitmen</span>
                <span>◫ {s.ders_sayisi} ders</span>
              </div>
            </div>
          ))}

          {subeler.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: textMuted, fontSize: 13 }}>
              Henüz şube yok
            </div>
          )}
        </div>
      </div>

      {/* Sağ: şube detayı */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 32 }}>
        {!secili ? (
          <div style={{ textAlign: 'center', color: textMuted, marginTop: 80 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🏢</div>
            <div>Bir şube seçin</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 600, color: textPrimary }}>{secili.ad}</div>
                {secili.adres && <div style={{ fontSize: 13, color: textSec, marginTop: 4 }}>{secili.adres}</div>}
                {secili.tel && <div style={{ fontSize: 13, color: textSec }}>📞 {secili.tel}</div>}
                {secili.email && <div style={{ fontSize: 13, color: textSec }}>✉ {secili.email}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => giriYap(secili)} style={{
                  background: '#534AB7', color: 'white', border: 'none', borderRadius: 8,
                  padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500,
                }}>
                  🏢 Şubeye Gir
                </button>
                <button onClick={() => acModal('duzenle')} style={{
                  background: card, color: '#534AB7', border: '1px solid #534AB7', borderRadius: 8,
                  padding: '8px 16px', fontSize: 13, cursor: 'pointer',
                }}>
                  Düzenle
                </button>
                <button onClick={() => silSube(secili.id)} style={{
                  background: card, color: '#e24b4a', border: '1px solid #e24b4a', borderRadius: 8,
                  padding: '8px 16px', fontSize: 13, cursor: 'pointer',
                }}>
                  Sil
                </button>
              </div>
            </div>

            {/* Mobil görünürlük */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '12px 16px', background: cardAlt, borderRadius: 8, border: `1px solid ${border}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#e0e0e0' : '#333' }}>Mobil uygulamada göster</div>
                <div style={{ fontSize: 11, color: textSec, marginTop: 2 }}>Öğrenci kayıt formunda bu şube seçilebilir</div>
              </div>
              <button
                onClick={async () => {
                  const yeni = !secili.mobilde_goster;
                  try {
                    await api.patch(`/api/subeler/${secili.id}/mobilde-goster`, { aktif: yeni });
                    const guncellendi = { ...secili, mobilde_goster: yeni };
                    setSecili(guncellendi);
                    setSubeler(prev => prev.map(s => s.id === secili.id ? guncellendi : s));
                  } catch (e: any) { alert(e?.response?.data?.hata || 'Güncellenemedi'); }
                }}
                style={{
                  padding: '7px 18px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: secili.mobilde_goster ? '#534AB7' : (isDark ? '#2a2a2a' : '#eee'),
                  color: secili.mobilde_goster ? 'white' : textSec,
                }}>
                {secili.mobilde_goster ? 'Görünüyor' : 'Gizli'}
              </button>
            </div>

            {/* İstatistikler */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 32 }}>
              {[
                { label: 'Öğrenci', val: secili.ogrenci_sayisi, icon: '◎' },
                { label: 'Eğitmen', val: secili.egitmen_sayisi, icon: '◐' },
                { label: 'Ders', val: secili.ders_sayisi, icon: '◫' },
                { label: 'Yönetici', val: secili.yonetici_sayisi, icon: '🔑' },
              ].map(item => (
                <div key={item.label} style={{
                  background: card, borderRadius: 10, padding: '16px 20px',
                  border: `1px solid ${border}`,
                }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>{item.icon}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: textPrimary }}>{item.val}</div>
                  <div style={{ fontSize: 12, color: textMuted }}>{item.label}</div>
                </div>
              ))}
            </div>

            {/* Yöneticiler */}
            <div style={{ background: card, borderRadius: 10, border: `1px solid ${border}`, padding: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 16, color: textPrimary }}>Şube Yöneticileri</div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <input
                  placeholder="E-posta adresi"
                  value={yeniYoneticiEmail}
                  onChange={e => setYeniYoneticiEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && yoneticiEkle()}
                  style={{
                    flex: 1, border: `1px solid ${border}`, borderRadius: 8,
                    padding: '8px 12px', fontSize: 13, outline: 'none',
                    background: card, color: textPrimary,
                  }}
                />
                <button onClick={yoneticiEkle} style={{
                  background: '#534AB7', color: 'white', border: 'none', borderRadius: 8,
                  padding: '8px 16px', fontSize: 13, cursor: 'pointer',
                }}>
                  Ekle
                </button>
              </div>

              {yoneticiler.length === 0 ? (
                <div style={{ color: textMuted, fontSize: 13, textAlign: 'center', padding: '12px 0' }}>
                  Henüz yönetici atanmamış
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {yoneticiler.map(y => {
                    const izinler = y.izinler || {};
                    return (
                      <div key={y.id} style={{
                        padding: '12px 14px', background: isDark ? '#1c1c1c' : '#f9f9fc', borderRadius: 8,
                        border: `1px solid ${border}`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                              width: 30, height: 30, borderRadius: '50%', background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE',
                              color: '#3C3489', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 12, fontWeight: 600,
                            }}>
                              {y.email.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ fontSize: 13, color: isDark ? '#e0e0e0' : '#333' }}>{y.email}</div>
                          </div>
                          <button onClick={() => yoneticiSil(y.id)} style={{
                            background: 'none', border: 'none', color: '#e24b4a',
                            cursor: 'pointer', fontSize: 13, padding: '2px 8px',
                          }}>
                            Kaldır
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {IZIN_ETIKETLER.map(({ key, label }) => {
                            const aktif = !!(izinler[key]);
                            return (
                              <button
                                key={key}
                                onClick={() => izinToggle(y.id, key, izinler)}
                                style={{
                                  padding: '3px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                                  border: `1px solid ${aktif ? '#534AB7' : (isDark ? '#3a3a3a' : '#ddd')}`,
                                  background: aktif ? (isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE') : card,
                                  color: aktif ? '#3C3489' : textSec,
                                  fontWeight: aktif ? 600 : 400,
                                  transition: 'all .15s',
                                }}
                              >
                                {aktif ? '✓ ' : ''}{label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal: Yeni / Düzenle */}
      {modal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setModal(null)}>
          <div style={{
            background: card, borderRadius: 14, padding: 28, width: 420, maxWidth: '95vw',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 20, color: textPrimary }}>
              {modal === 'yeni' ? 'Yeni Şube' : 'Şubeyi Düzenle'}
            </div>

            {[
              { label: 'Şube Adı *', key: 'ad', placeholder: 'Taksim (333) Şubesi' },
              { label: 'Adres', key: 'adres', placeholder: 'Şube adresi' },
              { label: 'Telefon', key: 'tel', placeholder: '+90 212 ...' },
              { label: 'E-posta', key: 'email', placeholder: 'sube@tango.istanbul' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: textSec, display: 'block', marginBottom: 4 }}>{f.label}</label>
                <input
                  value={(form as any)[f.key]}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  style={{
                    width: '100%', boxSizing: 'border-box', border: `1px solid ${border}`,
                    borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none',
                    background: card, color: textPrimary,
                  }}
                />
              </div>
            ))}

            {hata && <div style={{ color: '#e24b4a', fontSize: 13, marginBottom: 12 }}>{hata}</div>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={() => setModal(null)} style={{
                border: `1px solid ${border}`, background: card, borderRadius: 8,
                padding: '9px 18px', fontSize: 13, cursor: 'pointer', color: textPrimary,
              }}>
                İptal
              </button>
              <button onClick={kaydet} disabled={kaydediyor} style={{
                background: '#534AB7', color: 'white', border: 'none', borderRadius: 8,
                padding: '9px 18px', fontSize: 13, cursor: 'pointer', fontWeight: 500,
                opacity: kaydediyor ? 0.6 : 1,
              }}>
                {kaydediyor ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subeler;
